import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HeaderComponent } from '../header/header.component';

type Row = Record<string, any>;

/**
 * The person's chats inside "View as" (2 Nov 2026): read on the server (`chats/audit/…`), because a
 * View-as session never gets a chat sign-in of its own. Nothing here can send, react, edit, pin,
 * upload or change a chat — there are no such controls and the server refuses them anyway.
 */
@Component({
  selector: 'app-view-as-chats',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent],
  templateUrl: './view-as-chats.component.html',
  styleUrls: ['./view-as-chats.component.scss'],
})
export class ViewAsChatsComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly route = inject(ActivatedRoute);

  chats: Row[] = [];
  loading = true;
  error = '';
  query = '';

  active: Row | null = null;
  messages: Row[] = [];
  page = 1;
  hasOlder = false;
  loadingMessages = false;
  messagesError = '';

  async ngOnInit(): Promise<void> {
    try {
      const res: any = await firstValueFrom(this.http.get(`${environment.host}chats/audit/list`));
      this.chats = Array.isArray(res?.data) ? res.data : [];
    } catch (e: any) {
      this.error = e?.error?.message || 'Could not load the chats';
    } finally {
      this.loading = false;
    }
    const wanted = this.route.snapshot.queryParamMap.get('channelId');
    const first = wanted ? this.chats.find((c) => c.channelId === wanted) : null;
    if (first) void this.open(first);
  }

  get filtered(): Row[] {
    const q = this.query.trim().toLowerCase();
    return q ? this.chats.filter((c) => this.name(c).toLowerCase().includes(q)) : this.chats;
  }

  name(c: Row): string {
    return (c.type === 'group' ? c.groupName : c.displayName || c.userName) || 'Chat';
  }

  avatar(c: Row): string {
    return (c.type === 'group' ? c.groupImg : c.profileImage) || 'assets/images/default-avatar.png';
  }

  preview(c: Row): string {
    const m = c.lastMessage;
    if (!m) return '';
    if (typeof m === 'string') return m;
    return m.message || (m.file ? 'File' : '');
  }

  async open(c: Row): Promise<void> {
    this.active = c;
    this.messages = [];
    this.page = 1;
    this.hasOlder = false;
    await this.load(1);
  }

  async loadOlder(): Promise<void> {
    if (this.hasOlder && !this.loadingMessages) await this.load(this.page + 1);
  }

  private async load(page: number): Promise<void> {
    const chat = this.active;
    if (!chat) return;
    this.loadingMessages = true;
    this.messagesError = '';
    try {
      const res: any = await firstValueFrom(
        this.http.get(`${environment.host}chats/audit/${encodeURIComponent(chat.channelId)}/messages`, {
          params: { page: String(page), limit: '50' },
        })
      );
      if (this.active !== chat) return;
      const data = res?.data || {};
      const batch: Row[] = Array.isArray(data.messages) ? data.messages : [];
      this.messages = page === 1 ? batch : [...batch, ...this.messages];
      this.page = page;
      this.hasOlder = !!data.hasOlder;
    } catch (e: any) {
      this.messagesError = e?.error?.message || 'Could not load the messages';
    } finally {
      this.loadingMessages = false;
    }
  }

  isSystem(m: Row): boolean {
    return m?.meta?.type === 'system';
  }

  mine(m: Row): boolean {
    return m?.sender_id === localStorage.getItem('guestID');
  }

  fileUrl(m: Row): string {
    const f = m?.file;
    if (!f) return '';
    if (typeof f === 'string') return f;
    if (Array.isArray(f)) return String(f[0]?.url || f[0] || '');
    return String(f.url || '');
  }

  fileName(m: Row): string {
    const url = this.fileUrl(m);
    return m?.meta?.ogFileName || decodeURIComponent(url.split('?')[0].split('/').pop() || 'File');
  }

  isImage(m: Row): boolean {
    const kind = String(m?.meta?.contentType || '');
    return kind === 'image' || /\.(png|jpe?g|gif|webp)(\?|$)/i.test(this.fileUrl(m));
  }

  when(m: Row): string {
    const d = new Date(m?.createdAt || 0);
    return isNaN(d.getTime())
      ? ''
      : d.toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  }
}
